import { BadgeCheck, BadgeIndianRupee, Camera, Check, CircleUserRound, FileCheck2, ScanLine, ThumbsUp } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import { COUNTRIES } from "../Visa";
import baliImage from "../../../../assets/Places/dubai.png";
import czechImage from "../../../../assets/Places/london.png";
import turkeyImage from "../../../../assets/holidays/thailand.png";
import hungaryImage from "../../../../assets/Places/paris.png";
import icelandImage from "../../../../assets/hero-top/activities-1.png";
import singaporeImage from "../../../../assets/Places/singapore.png";
import japanImage from "../../../../assets/Places/tokyo.png";
import malaysiaImage from "../../../../assets/Places/lumpur.png";
import "./form.css";

const countryImages = {
  AE: baliImage,
  CZ: czechImage,
  TR: turkeyImage,
  HU: hungaryImage,
  IS: icelandImage,
  SG: singaporeImage,
  JP: japanImage,
  MY: malaysiaImage,
  TH: turkeyImage,
  ID: baliImage,
};

const visaTypeContent = {
  tourist: {
    label: "Tourist",
    price: 9260,
    note: "Your visa picture really matters. Please ensure that your photo captures your charming smile in a bright background and your face is clearly visible.",
  },
  business: {
    label: "Business",
    price: 11250,
    note: "Upload a clear professional photo for your business visa application. Keep the background plain and your face fully visible.",
  },
  student: {
    label: "Student",
    price: 13400,
    note: "Use a recent student visa photo with a neutral expression, plain background, and no shadows across your face.",
  },
  medical: {
    label: "Medical",
    price: 10500,
    note: "Add a clear medical visa photo where your face is centered, bright, and easy for embassy verification.",
  },
  transit: {
    label: "Transit",
    price: 5200,
    note: "Upload a sharp transit visa photo with your head centered and a clean, light background.",
  },
  work: {
    label: "Work",
    price: 15600,
    note: "Use a recent work visa photo with formal framing, even lighting, and no glare or blur.",
  },
};

const visaRules = {
  Schengen: { validity: "90 Days", entry: "Multiple" },
  "E-Visa": { validity: "30 Days", entry: "Single" },
  "Sticker Visa": { validity: "180 Days", entry: "Single" },
  VOA: { validity: "30 Days", entry: "Single" },
};

const baseSteps = [
  { number: 1, title: "Select Date", icon: Check },
  { number: 2, title: "Upload Picture", icon: Camera },
  { number: 3, title: "Scan Passport", icon: ScanLine },
  { number: 4, title: "Traveller Details", icon: CircleUserRound },
];

const photoRules = [
  "Position your head into the oval",
  "Make sure you're in a well-lit area",
  "Remove glasses",
  "Avoid glares and blurs",
];

const formatTravelDate = value => {
  if (!value) return "26 Jun 2026, 02:59 PM";

  const date = new Date(`${value}T14:59:00`);
  if (Number.isNaN(date.getTime())) return "26 Jun 2026, 02:59 PM";

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).replace(",", "");
};

const formatPrice = value =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(value || 0);

export default function VisaApplicationForm() {
  const [params] = useSearchParams();
  const countryCode = (params.get("country") || "ae").toUpperCase();
  const travellerCount = Math.max(1, Math.min(Number(params.get("travellers") || 1), 9));
  const selectedType = (params.get("type") || "tourist").toLowerCase();
  const travelDate = params.get("date");
  const [activeAdult, setActiveAdult] = useState(1);
  const [stage, setStage] = useState("photo");
  const [uploadedPhotos, setUploadedPhotos] = useState({});
  const [passportFile, setPassportFile] = useState("");
  const [travellerDetails, setTravellerDetails] = useState({
    fullName: "",
    passportNumber: "",
    email: "",
    phone: "",
  });
  const photoInputRef = useRef(null);
  const passportInputRef = useRef(null);

  useEffect(() => {
    AOS.init({ duration: 650, once: true, easing: "ease-out-cubic", offset: 50 });
    const timer = setTimeout(() => AOS.refresh(), 80);
    return () => clearTimeout(timer);
  }, []);

  const country = useMemo(
    () => COUNTRIES.find(item => item.code === countryCode) || COUNTRIES.find(item => item.code === "AE") || COUNTRIES[0],
    [countryCode]
  );

  const typeContent = visaTypeContent[selectedType] || visaTypeContent.tourist;
  const visaDetail = visaRules[country.type] || visaRules["E-Visa"];
  const estimatedDate = formatTravelDate(travelDate);
  const heroImage = countryImages[country.code] || baliImage;
  const adultTabs = Array.from({ length: travellerCount }, (_, index) => index + 1);
  const titleCountry = country.name === "Indonesia / Bali" ? "Bali" : country.name;
  const visaPrice = typeContent.price || country.fee;
  const totalVisaPrice = visaPrice * travellerCount;
  const uploadedPhotoCount = Object.keys(uploadedPhotos).length;
  const stepStatus = useMemo(() => baseSteps.map(step => {
    if (step.number === 1) return { ...step, status: "Completed", tone: "done" };
    if (step.number === 2) {
      return stage === "photo"
        ? { ...step, status: "In Process", tone: "active" }
        : { ...step, status: "Completed", tone: "done" };
    }
    if (step.number === 3) {
      if (stage === "photo") return { ...step, status: "Pending", tone: "pending" };
      return stage === "passport"
        ? { ...step, status: "In Process", tone: "active" }
        : { ...step, status: "Completed", tone: "done" };
    }
    if (stage === "details") return { ...step, status: "In Process", tone: "active" };
    return { ...step, status: "Pending", tone: "pending" };
  }), [stage]);

  const handlePhotoUpload = event => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadedPhotos(current => ({ ...current, [activeAdult]: file.name }));
    setStage("passport");
  };

  const handlePassportUpload = event => {
    const file = event.target.files?.[0];
    if (!file) return;
    setPassportFile(file.name);
    setStage("details");
  };

  const updateTravellerDetails = event => {
    const { name, value } = event.target;
    setTravellerDetails(current => ({ ...current, [name]: value }));
  };

  const panelTitle = stage === "photo"
    ? "Click or Upload Your Photo"
    : stage === "passport"
      ? "Upload Your Passport"
      : "Traveller Details";

  const guideTitle = stage === "photo"
    ? "Time For Your Close-Up"
    : stage === "passport"
      ? "Scan Passport Clearly"
      : "Complete Your Profile";

  const guideText = stage === "photo"
    ? typeContent.note
    : stage === "passport"
      ? "Upload a clear passport front page. Make sure all text, photo, passport number, and expiry date are visible."
      : "Fill traveller details exactly as printed on the passport to avoid embassy verification delays.";

  const guideRules = stage === "photo" ? photoRules : stage === "passport"
    ? ["Upload front page of passport", "Keep all corners visible", "Avoid blur and reflections", "File should be readable"]
    : ["Use passport name only", "Double-check passport number", "Add reachable contact details", "Review before submitting"];

  return (
    <main className="visa-form-page">
      <aside className="visa-form-steps" aria-label="Visa application progress">
        {stepStatus.map((step, index) => {
          const Icon = step.icon;
          return (
            <div className={`visa-form-step visa-form-step--${step.tone}`} key={step.number}>
              <div className="visa-form-step-icon">
                <Icon size={22} />
              </div>
              {index < stepStatus.length - 1 && <span className="visa-form-step-line" />}
              <div className="visa-form-step-copy">
                <span>STEP {step.number}</span>
                <strong>{step.title}</strong>
                <em>{step.status}</em>
              </div>
            </div>
          );
        })}
      </aside>

      <section className="visa-form-workspace">
        <h1>Complete The Process In <strong>4 Easy Steps</strong></h1>

        <aside className="visa-photo-guide visa-form-hero mbt-animated-card" data-aos="fade-up" data-aos-delay="80">
          <div className="visa-form-hero-copy">
            <h2>{titleCountry} {typeContent.label} Visa For Indians</h2>
            <div className="visa-dynamic-summary">
              <span><FileCheck2 size={16} /> {country.type}</span>
              <span><BadgeIndianRupee size={16} /> {formatPrice(totalVisaPrice)}</span>
            </div>

            <div className="visa-guide-tags">
              <span>Validity: {visaDetail.validity}</span>
              <i />
              <span>Entry: {visaDetail.entry}</span>
            </div>

            <h3>{guideTitle}</h3>
            <p>{guideText}</p>

            <ul className="visa-photo-rules">
              {guideRules.map(rule => (
                <li key={rule}>
                  <Check size={16} />
                  <span>{rule}</span>
                </li>
              ))}
            </ul>
          </div>
          <img src={heroImage} alt={`${country.name} visa`} />
        </aside>

        <div className="visa-form-card mbt-animated-card" data-aos="fade-up">
          <section className="visa-photo-panel" data-aos="fade-right" data-aos-delay="80">
            <h2>{panelTitle}</h2>

            <div className="visa-estimated-date">
              <span><ThumbsUp size={21} /></span>
              <p>Estimated Date <strong>{estimatedDate.toUpperCase()}</strong></p>
            </div>

            <div className="visa-adult-row">
              <div className="visa-adult-tabs">
                {adultTabs.map(adult => (
                  <button
                    type="button"
                    className={adult === activeAdult ? "is-active" : ""}
                    onClick={() => setActiveAdult(adult)}
                    key={adult}
                  >
                    Adult {adult}
                  </button>
                ))}
              </div>
              <span>{uploadedPhotoCount || activeAdult}/{travellerCount} Adult Added</span>
            </div>

            {stage === "photo" && (
              <>
                <input
                  ref={photoInputRef}
                  className="visa-hidden-file"
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                />
                <button type="button" className="visa-photo-upload" onClick={() => photoInputRef.current?.click()}>
                  <Camera size={46} />
                  <span>{uploadedPhotos[activeAdult] || `Click or upload photo for Adult ${activeAdult}`}</span>
                </button>
              </>
            )}

            {stage === "passport" && (
              <div className="visa-upload-stage">
                <input
                  ref={passportInputRef}
                  className="visa-hidden-file"
                  type="file"
                  accept="image/*,.pdf"
                  onChange={handlePassportUpload}
                />
                <button type="button" className="visa-passport-upload" onClick={() => passportInputRef.current?.click()}>
                  <ScanLine size={52} />
                  <strong>Upload Passport Front Page</strong>
                  <span>{passportFile || "JPG, PNG or PDF accepted"}</span>
                </button>
                <p>Photo uploaded successfully. Please upload passport to continue.</p>
              </div>
            )}

            {stage === "details" && (
              <form className="visa-details-form" onSubmit={event => event.preventDefault()}>
                <div className="visa-form-success">
                  <BadgeCheck size={20} />
                  <span>Passport uploaded successfully. Complete traveller details now.</span>
                </div>
                <label>
                  <span>Full Name</span>
                  <input name="fullName" value={travellerDetails.fullName} onChange={updateTravellerDetails} placeholder="Enter name as per passport" />
                </label>
                <label>
                  <span>Passport Number</span>
                  <input name="passportNumber" value={travellerDetails.passportNumber} onChange={updateTravellerDetails} placeholder="Enter passport number" />
                </label>
                <label>
                  <span>Email Address</span>
                  <input name="email" type="email" value={travellerDetails.email} onChange={updateTravellerDetails} placeholder="Enter email address" />
                </label>
                <label>
                  <span>Mobile Number</span>
                  <input name="phone" value={travellerDetails.phone} onChange={updateTravellerDetails} placeholder="Enter mobile number" />
                </label>
                <button type="submit">Continue</button>
              </form>
            )}
          </section>
        </div>
      </section>
    </main>
  );
}
